import { getNlsString } from '../nls/nlsRuntime'
import type { NlsInterpolationValues } from '../nls/nlsTypes'
import type { NlsKey } from '../nls/strings_nls'
import type { CosmeticRarity, SpinTier } from '../types/schema'

export type ExperienceNlsT = (key: NlsKey, values?: NlsInterpolationValues) => string

const defaultT: ExperienceNlsT = (key, values) => getNlsString(key, values)

export type YourPageTabNls = 'IDENTITY' | 'PROFILE' | 'ACHIEVEMENTS' | 'CHARACTER'
export type TrackFilterNls = 'all' | 'in-progress' | 'claimable' | 'completed' | 'ascended'
export type NotificationGroupNls = 'Today' | 'Yesterday' | 'Older'
export type RaidArenaNls = 'BATTLEFIELD' | 'WAR_ROOM' | 'CONSTELLATION' | 'TIMELINE'

const YOUR_TAB_KEYS: Record<YourPageTabNls, NlsKey> = {
  IDENTITY: 'NLSExperienceYourTabDetails',
  PROFILE: 'NLSExperienceYourTabProfile',
  ACHIEVEMENTS: 'NLSExperienceYourTabAchievements',
  CHARACTER: 'NLSExperienceYourTabCharacter',
}

const RARITY_KEYS: Record<CosmeticRarity, NlsKey> = {
  COMMON: 'NLSExperienceRarityCommon',
  RARE: 'NLSExperienceRarityRare',
  EPIC: 'NLSExperienceRarityEpic',
  LEGENDARY: 'NLSExperienceRarityLegendary',
}

const SPIN_TIER_KEYS: Record<SpinTier, NlsKey> = {
  LUCKY_SPIN: 'NLSExperienceSpinTierLucky',
  MEGA_SPIN: 'NLSExperienceSpinTierMega',
  GIGA_SPIN: 'NLSExperienceSpinTierGiga',
}

const TRACK_FILTER_KEYS: Record<TrackFilterNls, NlsKey> = {
  all: 'NLSExperienceFilterAll',
  'in-progress': 'NLSExperienceFilterInProgress',
  claimable: 'NLSExperienceFilterClaimable',
  completed: 'NLSExperienceFilterCompleted',
  ascended: 'NLSExperienceFilterAscended',
}

const NOTIFICATION_GROUP_KEYS: Record<NotificationGroupNls, NlsKey> = {
  Today: 'NLSExperienceNotificationGroupToday',
  Yesterday: 'NLSExperienceNotificationGroupYesterday',
  Older: 'NLSExperienceNotificationGroupOlder',
}

const RAID_ARENA_KEYS: Record<RaidArenaNls, { label: NlsKey; description: NlsKey }> = {
  BATTLEFIELD: { label: 'NLSExperienceRaidArenaBattlefield', description: 'NLSExperienceRaidArenaBattlefieldDetail' },
  WAR_ROOM: { label: 'NLSExperienceRaidArenaWarRoom', description: 'NLSExperienceRaidArenaWarRoomDetail' },
  CONSTELLATION: { label: 'NLSExperienceRaidArenaConstellation', description: 'NLSExperienceRaidArenaConstellationDetail' },
  TIMELINE: { label: 'NLSExperienceRaidArenaTimeline', description: 'NLSExperienceRaidArenaTimelineDetail' },
}

export const getYourPageTabLabel = (tab: YourPageTabNls, t: ExperienceNlsT = defaultT) => t(YOUR_TAB_KEYS[tab])

export const getRarityLabel = (rarity: CosmeticRarity, t: ExperienceNlsT = defaultT) => t(RARITY_KEYS[rarity])

export const getSpinTierLabel = (tier: SpinTier, t: ExperienceNlsT = defaultT) => t(SPIN_TIER_KEYS[tier])

export const getTrackFilterLabel = (filter: TrackFilterNls, t: ExperienceNlsT = defaultT) => t(TRACK_FILTER_KEYS[filter])

export const getNotificationGroupLabel = (group: NotificationGroupNls, t: ExperienceNlsT = defaultT) =>
  t(NOTIFICATION_GROUP_KEYS[group])

export const getNotificationRelativeSubtitle = (
  group: NotificationGroupNls,
  days: number | null,
  t: ExperienceNlsT = defaultT,
) => {
  if (group === 'Today') return t('NLSExperienceNotificationRelativeToday')
  if (group === 'Yesterday') return t('NLSExperienceNotificationRelativeYesterday')
  return days ? t('NLSExperienceNotificationRelativeDaysAgo', { count: days }) : ''
}

export const getRaidArenaMeta = (arena: RaidArenaNls, t: ExperienceNlsT = defaultT) => {
  const keys = RAID_ARENA_KEYS[arena]
  return { label: t(keys.label), description: t(keys.description) }
}

